import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  RefreshCw,
  Calendar,
  Download,
  ChevronDown,
  ChevronRight,
  Eye,
  FileSpreadsheet,
  AlertTriangle,
  Clock,
  TrendingUp,
  IndianRupee,
  Package,
  Users,
  Store,
  Truck,
  BarChart3,
  ShoppingCart,
  Percent,
  RotateCcw,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Business terms that must NOT be translated (English only)
const DO_NOT_TRANSLATE_TERMS = [
  'Sales', 'Revenue', 'Stock', 'SKU', 'Margin', 'Discount', 'Returns', 
  'Footfall', 'Target', 'Sell-through', 'Inventory', 'Vendor', 'GM', 
  'ASP', 'AOV', 'MRP', 'GRN', 'OTB', 'OTIF', 'SLA', 'KPI', 'WOS', 
  'SOH', 'COGS', 'GMROI', 'UPT', 'ATV', 'LFL', 'EBITDA', 'PO'
];

// Owner roles mapping
const OWNER_ROLES = {
  stockout: 'Supply Chain',
  inventory: 'Supply Chain',
  dead_stock: 'Merch / CFO',
  margin: 'CFO',
  discount: 'CFO / Sales',
  returns: 'Store Ops',
  customer: 'Sales / CRM',
  vendor: 'Procurement',
  store: 'Store Ops',
  sales: 'Sales',
  default: 'Operations'
};

// Agent to dashboard mapping
const AGENT_DASHBOARD_MAP = {
  'StockSense': '/dashboard/stock-sense',
  'SalesIQ': '/dashboard/sales-iq',
  'Customer360': '/dashboard/customer-360',
  'ProductMix': '/dashboard/product-mix',
  'StorePulse': '/dashboard/store-pulse',
  'VendorLink': '/dashboard/vendor-link',
  'PromoIQ': '/dashboard/promo-iq',
  'DemandFlow': '/dashboard/demand-flow',
  'ReturnIQ': '/dashboard/return-iq',
  'ExecutiveBrain': '/dashboard/executive-brain'
};

const ExecutiveSummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState('en-IN');
  const [translatedContent, setTranslatedContent] = useState({});
  const [translating, setTranslating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchLatestSummary();
    // Load saved locale
    const savedLocale = localStorage.getItem('preferredLocale');
    if (savedLocale) {
      setSelectedLocale(savedLocale);
    }
  }, []);

  // Translate content when language changes
  useEffect(() => {
    if (summary && selectedLocale !== 'en-IN') {
      translateContent();
    } else if (selectedLocale === 'en-IN') {
      setTranslatedContent({});
    }
  }, [selectedLocale, summary]);

  const fetchLatestSummary = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/executive-summary/latest`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'success' && response.data.summary) {
        setSummary(response.data.summary);
      } else {
        setSummary(null);
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
      toast.error('Failed to load executive brief');
    } finally {
      setLoading(false);
    }
  };

  const generateSummary = async () => {
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/executive-summary/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchLatestSummary();
      toast.success('Executive brief refreshed');
    } catch (err) {
      toast.error('Failed to refresh brief');
    } finally {
      setGenerating(false);
    }
  };

  const translateContent = async () => {
    if (!summary || selectedLocale === 'en-IN') return;
    
    setTranslating(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build text to translate (narrative sections only)
      const textsToTranslate = {
        verdict: summary.executive_overview || '',
        outlook_7: getOutlookBullets('7days').join('. '),
        outlook_30: getOutlookBullets('30days').join('. '),
        outlook_90: getOutlookBullets('90days').join('. ')
      };
      
      // Add decision descriptions
      const decisions = buildDecisionStack();
      decisions.forEach((d, i) => {
        textsToTranslate[`decision_${i}_title`] = d.title;
        textsToTranslate[`decision_${i}_why`] = d.whyMatters;
      });
      
      // Translate via chatbot endpoint (reusing existing translation)
      const translations = {};
      for (const [key, text] of Object.entries(textsToTranslate)) {
        if (text) {
          try {
            const response = await axios.post(
              `${BACKEND_URL}/api/chatbot/translate`,
              { text, target_locale: selectedLocale },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            translations[key] = response.data?.translated_text || text;
          } catch {
            translations[key] = text; // Fallback to English
          }
        }
      }
      
      setTranslatedContent(translations);
    } catch (err) {
      console.error('Translation error:', err);
      // Fallback to English silently
      setTranslatedContent({});
    } finally {
      setTranslating(false);
    }
  };

  const handleLanguageChange = useCallback((locale) => {
    setSelectedLocale(locale);
    localStorage.setItem('preferredLocale', locale);
  }, []);

  // Get translated text or fallback to English
  const getTranslated = (key, fallback) => {
    if (selectedLocale === 'en-IN') return fallback;
    return translatedContent[key] || fallback;
  };

  // Parse actions and risks from summary
  const safeParseArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data.replace(/'/g, '"'));
      } catch {
        return data.split(',').map(s => s.trim()).filter(Boolean);
      }
    }
    return [];
  };

  // Extract agent name from action text
  const extractAgent = (text) => {
    const match = text.match(/\[([^\]]+)\]/);
    return match ? match[1] : null;
  };

  // Determine owner role based on keywords
  const determineOwner = (text) => {
    const lowerText = text.toLowerCase();
    if (lowerText.includes('stockout') || lowerText.includes('reorder')) return OWNER_ROLES.stockout;
    if (lowerText.includes('dead stock') || lowerText.includes('aged')) return OWNER_ROLES.dead_stock;
    if (lowerText.includes('margin') || lowerText.includes('profitability')) return OWNER_ROLES.margin;
    if (lowerText.includes('discount') || lowerText.includes('promo')) return OWNER_ROLES.discount;
    if (lowerText.includes('return')) return OWNER_ROLES.returns;
    if (lowerText.includes('customer') || lowerText.includes('churn')) return OWNER_ROLES.customer;
    if (lowerText.includes('vendor') || lowerText.includes('supplier')) return OWNER_ROLES.vendor;
    if (lowerText.includes('store')) return OWNER_ROLES.store;
    if (lowerText.includes('sales') || lowerText.includes('revenue')) return OWNER_ROLES.sales;
    return OWNER_ROLES.default;
  };

  // Determine timeline (P0/P1/P2)
  const determineTimeline = (text) => {
    const upper = text.toUpperCase();
    if (upper.includes('IMMEDIATE') || upper.includes('URGENT') || upper.includes('CRITICAL') || 
        upper.includes('STOCKOUT') || upper.includes('TODAY') || upper.includes('EXPEDITE')) {
      return { priority: 'P0', label: '0-7 days', color: 'red' };
    }
    if (upper.includes('THIS WEEK') || upper.includes('PRIORITY') || upper.includes('NEXT 30')) {
      return { priority: 'P1', label: '30 days', color: 'amber' };
    }
    return { priority: 'P2', label: '60-90 days', color: 'blue' };
  };

  // Extract money at stake from text
  const extractMoneyAtStake = (text) => {
    const matches = text.match(/₹[\d,]+(?:\.\d+)?(?:\s*(?:Cr|L|K|Lakhs?|Crores?))?/gi);
    return matches ? matches[0] : null;
  };

  // Build decision stack from actions (deduplicated, max 5)
  const buildDecisionStack = () => {
    const actions = safeParseArray(summary?.action_priorities || []);
    const risks = safeParseArray(summary?.operational_risks || summary?.risk_summary || []);
    
    const seenSignals = new Set();
    const decisions = [];
    
    // Process actions
    actions.forEach(action => {
      const text = typeof action === 'string' ? action : (action.text || String(action));
      const normalizedKey = text.toLowerCase().replace(/\[.*?\]/g, '').trim().substring(0, 50);
      
      if (seenSignals.has(normalizedKey)) return;
      seenSignals.add(normalizedKey);
      
      const agent = extractAgent(text);
      const timeline = determineTimeline(text);
      const owner = determineOwner(text);
      const money = extractMoneyAtStake(text);
      
      // Build "why this matters" from context
      let whyMatters = '';
      if (text.includes('stockout')) {
        whyMatters = 'Revenue at risk from lost sales. Customers may shift to competitors.';
      } else if (text.includes('dead stock') || text.includes('blocked capital')) {
        whyMatters = 'Working capital locked in non-moving inventory reduces cash flow.';
      } else if (text.includes('margin') || text.includes('discount')) {
        whyMatters = 'Excessive discounting erodes profitability and brand value.';
      } else if (text.includes('return')) {
        whyMatters = 'High return rates impact margins and customer satisfaction.';
      } else if (text.includes('churn') || text.includes('at risk')) {
        whyMatters = 'Customer attrition reduces historical customer value and market share.';
      } else {
        whyMatters = 'Business impact identified by AI analysis requiring attention.';
      }
      
      // Find related risk
      const relatedRisk = risks.find(r => {
        const riskText = typeof r === 'object' ? r.risk : r;
        return riskText?.toLowerCase().includes(normalizedKey.substring(0, 20));
      });
      
      decisions.push({
        title: text.replace(/\[.*?\]\s*/g, ''),
        whyMatters,
        owner,
        timeline,
        moneyAtStake: money,
        riskLevel: relatedRisk?.severity || (timeline.priority === 'P0' ? 'High' : 'Medium'),
        affectedCount: text.match(/(\d+)\s*(SKUs?|stores?|customers?)/i)?.[0] || null,
        agent,
        dashboardPath: agent ? AGENT_DASHBOARD_MAP[agent] : null
      });
    });
    
    // Sort by priority and limit to 5
    return decisions
      .sort((a, b) => a.timeline.priority.localeCompare(b.timeline.priority))
      .slice(0, 5);
  };

  // Build CFO exposure table data
  const buildExposureData = () => {
    const risks = safeParseArray(summary?.operational_risks || summary?.risk_summary || []);
    const overview = summary?.executive_overview || '';
    const inventory = summary?.inventory_summary || '';
    const revenue = summary?.revenue_summary || '';
    const margin = summary?.margin_summary || '';
    
    return {
      revenueRisk: {
        level: inventory.toLowerCase().includes('stockout') ? 'High' : 'Medium',
        detail: inventory.match(/(\d+)\s*SKUs?\s*(?:at\s*)?(?:critical\s*)?stockout/i)?.[0] || 
                (inventory.toLowerCase().includes('stockout') ? 'Stockout risk detected' : 'Within acceptable range'),
        source: 'StockSense'
      },
      capitalBlocked: {
        value: extractMoneyAtStake(inventory) || 'N/A',
        detail: inventory.match(/(\d+)\s*(?:units?\s*)?(?:of\s*)?dead\s*stock/i)?.[0] || 'See inventory dashboard',
        source: 'StockSense'
      },
      marginLeakage: {
        level: margin.toLowerCase().includes('eroding') || margin.includes('43.2%') ? 'High' : 'Medium',
        detail: margin.match(/(\d+)\s*SKUs?\s*promo[- ]dependent/i)?.[0] || 
                margin.match(/\d+\.?\d*%.*incentive/i)?.[0] || 'Monitor promotional dependency',
        source: 'PromoIQ / SalesIQ'
      },
      returnsRisk: {
        level: revenue.toLowerCase().includes('return rate') && parseFloat(revenue.match(/(\d+\.?\d*)%\s*return/)?.[1] || 0) > 10 ? 'High' : 'Low',
        detail: revenue.match(/(\d+\.?\d*)%\s*return\s*rate/i)?.[0] || 'Within normal range',
        source: 'ReturnIQ'
      },
      vendorRisk: {
        level: 'Medium',
        detail: 'Vendor dependency analysis available in VendorLink',
        source: 'VendorLink'
      }
    };
  };

  // Build 7/30/90 day outlook bullets
  const getOutlookBullets = (period) => {
    const actions = safeParseArray(summary?.action_priorities || []);
    const outlook = summary?.forward_outlook || '';
    
    const bullets = {
      '7days': [],
      '30days': [],
      '90days': []
    };
    
    actions.forEach(action => {
      const text = typeof action === 'string' ? action : (action.text || String(action));
      const timeline = determineTimeline(text);
      const cleanText = text.replace(/\[.*?\]\s*/g, '');
      
      if (timeline.priority === 'P0') {
        bullets['7days'].push(cleanText);
      } else if (timeline.priority === 'P1') {
        bullets['30days'].push(cleanText);
      } else {
        bullets['90days'].push(cleanText);
      }
    });
    
    // Add context-based defaults if empty
    if (bullets['7days'].length === 0) bullets['7days'].push('Monitor critical stockout SKUs');
    if (bullets['30days'].length === 0) bullets['30days'].push('Review inventory optimization recommendations');
    if (bullets['90days'].length === 0) bullets['90days'].push('Implement structural process improvements');
    
    return bullets[period]?.slice(0, 4) || [];
  };

  // Build evidence sections
  const getEvidenceSections = () => {
    return [
      { 
        id: 'sales', 
        title: 'Sales & Revenue', 
        icon: IndianRupee,
        content: summary?.revenue_summary || 'No data available',
        agents: ['SalesIQ'],
        color: 'blue'
      },
      { 
        id: 'inventory', 
        title: 'Inventory & Working Capital', 
        icon: Package,
        content: summary?.inventory_summary || 'No data available',
        agents: ['StockSense'],
        color: 'amber'
      },
      { 
        id: 'margin', 
        title: 'Margin & Promotions', 
        icon: Percent,
        content: summary?.margin_summary || summary?.profitability_summary || 'No data available',
        agents: ['PromoIQ', 'SalesIQ'],
        color: 'green'
      },
      { 
        id: 'customers', 
        title: 'Customers & Retention', 
        icon: Users,
        content: summary?.customer_summary || 'No data available',
        agents: ['Customer360'],
        color: 'purple'
      },
      { 
        id: 'stores', 
        title: 'Stores & Operations', 
        icon: Store,
        content: 'Store performance data available in StorePulse dashboard',
        agents: ['StorePulse'],
        color: 'teal'
      },
      { 
        id: 'vendors', 
        title: 'Vendors & Supply Chain', 
        icon: Truck,
        content: 'Vendor analysis available in VendorLink dashboard',
        agents: ['VendorLink'],
        color: 'orange'
      },
      { 
        id: 'demand', 
        title: 'Demand & Forecast Signals', 
        icon: BarChart3,
        content: summary?.forward_outlook || 'Forecast data available in DemandFlow dashboard',
        agents: ['DemandFlow'],
        color: 'indigo'
      }
    ];
  };

  // Download executive pack (English only)
  const downloadExecutivePack = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/executive-summary/export/executive-pack`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Executive-Brief-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Executive Pack downloaded (English)');
    } catch (err) {
      toast.error('Download failed');
    }
  };

  // Download evidence for specific section
  const downloadEvidence = async (sectionId, agents) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/executive-summary/export/agent-evidence?agents=${agents.join(',')}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Evidence-${sectionId}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Evidence downloaded (English)');
    } catch (err) {
      toast.error('Download failed');
    }
  };

  // Navigate to agent dashboard
  const viewEvidence = (agent) => {
    const path = AGENT_DASHBOARD_MAP[agent];
    if (path) {
      navigate(path);
    } else {
      navigate('/agents');
    }
  };

  // Toggle accordion section
  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="executive-brief-loading">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4 text-slate-600" />
          <p className="text-slate-600">Loading Executive Brief...</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="container mx-auto px-4 py-8" data-testid="executive-brief-empty">
        <div className="max-w-2xl mx-auto text-center">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 text-slate-400" />
          <h2 className="text-2xl font-bold mb-4 text-slate-800">No Executive Brief Available</h2>
          <p className="text-slate-600 mb-6">
            Generate your executive brief to get leadership-level insights for decision-making.
          </p>
          <Button onClick={generateSummary} disabled={generating} data-testid="generate-brief-btn">
            {generating ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Generating...</> : 'Generate Executive Brief'}
          </Button>
        </div>
      </div>
    );
  }

  const decisions = buildDecisionStack();
  const exposure = buildExposureData();
  const evidenceSections = getEvidenceSections();

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl" data-testid="executive-brief-page">
      
      {/* ===== A) HEADER BAR ===== */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 pb-4 border-b border-slate-200">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Executive Brief</h1>
          <p className="text-sm text-slate-500 flex items-center mt-1">
            <Calendar className="w-4 h-4 mr-1" />
            As of {summary.generated_at ? new Date(summary.generated_at).toLocaleDateString('en-IN', {
              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
            }) : 'N/A'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSelector onLanguageChange={handleLanguageChange} />
          <Button
            variant="outline"
            size="sm"
            onClick={generateSummary}
            disabled={generating}
            data-testid="refresh-brief-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${generating ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={downloadExecutivePack}
            data-testid="download-executive-pack-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Export Executive Pack (English)
          </Button>
        </div>
      </div>

      {/* Translation indicator */}
      {translating && (
        <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center text-sm text-blue-700">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Translating content...
        </div>
      )}

      {/* ===== B) EXECUTIVE VERDICT ===== */}
      <section className="mb-8" data-testid="executive-verdict-section">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Executive Verdict</h2>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
          <p className="text-slate-700 leading-relaxed text-base" style={{ maxHeight: '150px', overflow: 'hidden' }}>
            {getTranslated('verdict', summary.executive_overview || 'Business assessment in progress. Run AI agents to generate insights.')}
          </p>
        </div>
      </section>

      {/* ===== C) DECISION STACK ===== */}
      <section className="mb-8" data-testid="decision-stack-section">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Decision Stack</h2>
        <div className="space-y-3">
          {decisions.length === 0 ? (
            <p className="text-slate-500 text-sm p-4 bg-slate-50 rounded-lg">No immediate decisions required. Business operations are stable.</p>
          ) : (
            decisions.map((decision, idx) => (
              <Card 
                key={idx} 
                className={`border-l-4 ${
                  decision.timeline.color === 'red' ? 'border-l-red-500 bg-red-50/30' :
                  decision.timeline.color === 'amber' ? 'border-l-amber-500 bg-amber-50/30' :
                  'border-l-blue-500 bg-blue-50/30'
                }`}
                data-testid={`decision-card-${idx}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Priority badge + Timeline */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${
                          decision.timeline.color === 'red' ? 'bg-red-600' :
                          decision.timeline.color === 'amber' ? 'bg-amber-500' : 'bg-blue-600'
                        }`}>
                          {decision.timeline.priority}
                        </span>
                        <span className="text-xs text-slate-500">{decision.timeline.label}</span>
                        <span className="text-xs text-slate-400">•</span>
                        <span className="text-xs text-slate-600 font-medium">{decision.owner}</span>
                      </div>
                      
                      {/* Decision Title */}
                      <h3 className="font-semibold text-slate-900 mb-1">
                        {getTranslated(`decision_${idx}_title`, decision.title)}
                      </h3>
                      
                      {/* Why this matters */}
                      <p className="text-sm text-slate-600 mb-2">
                        <span className="font-medium">Why this matters:</span>{' '}
                        {getTranslated(`decision_${idx}_why`, decision.whyMatters)}
                      </p>
                      
                      {/* Money at stake / Risk level */}
                      <div className="flex items-center gap-4 text-sm">
                        {decision.moneyAtStake ? (
                          <span className="text-slate-700">
                            <IndianRupee className="w-3 h-3 inline mr-1" />
                            <strong>{decision.moneyAtStake}</strong> at stake
                          </span>
                        ) : (
                          <span className={`${
                            decision.riskLevel === 'High' ? 'text-red-600' :
                            decision.riskLevel === 'Medium' ? 'text-amber-600' : 'text-slate-600'
                          }`}>
                            Risk: <strong>{decision.riskLevel}</strong>
                          </span>
                        )}
                        {decision.affectedCount && (
                          <span className="text-slate-500">({decision.affectedCount} affected)</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2">
                      {decision.agent && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => viewEvidence(decision.agent)}
                          data-testid={`view-evidence-${decision.agent}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View Evidence
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                        onClick={() => downloadEvidence('decision', decision.agent ? [decision.agent] : ['General'])}
                        data-testid={`download-evidence-${idx}`}
                      >
                        <FileSpreadsheet className="w-3 h-3 mr-1" />
                        Download (English)
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </section>

      {/* ===== D) FINANCIAL EXPOSURE & OPPORTUNITY (CFO TABLE) ===== */}
      <section className="mb-8" data-testid="financial-exposure-section">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Financial Exposure & Opportunity</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-3 font-semibold text-slate-700">Exposure Area</th>
                <th className="text-left p-3 font-semibold text-slate-700">Level / Value</th>
                <th className="text-left p-3 font-semibold text-slate-700">Detail</th>
                <th className="text-left p-3 font-semibold text-slate-700">Source</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-200">
                <td className="p-3 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2 text-red-500" />
                  Revenue Risk (Stockouts)
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    exposure.revenueRisk.level === 'High' ? 'bg-red-100 text-red-700' :
                    exposure.revenueRisk.level === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {exposure.revenueRisk.level}
                  </span>
                </td>
                <td className="p-3 text-slate-600">{exposure.revenueRisk.detail}</td>
                <td className="p-3 text-slate-500">{exposure.revenueRisk.source}</td>
              </tr>
              <tr className="border-t border-slate-200 bg-slate-50/50">
                <td className="p-3 flex items-center">
                  <Package className="w-4 h-4 mr-2 text-amber-500" />
                  Capital Blocked (Dead/Aged Stock)
                </td>
                <td className="p-3 font-semibold text-slate-800">{exposure.capitalBlocked.value}</td>
                <td className="p-3 text-slate-600">{exposure.capitalBlocked.detail}</td>
                <td className="p-3 text-slate-500">{exposure.capitalBlocked.source}</td>
              </tr>
              <tr className="border-t border-slate-200">
                <td className="p-3 flex items-center">
                  <Percent className="w-4 h-4 mr-2 text-orange-500" />
                  Margin Leakage (Discounts/Promos)
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    exposure.marginLeakage.level === 'High' ? 'bg-red-100 text-red-700' :
                    exposure.marginLeakage.level === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {exposure.marginLeakage.level}
                  </span>
                </td>
                <td className="p-3 text-slate-600">{exposure.marginLeakage.detail}</td>
                <td className="p-3 text-slate-500">{exposure.marginLeakage.source}</td>
              </tr>
              <tr className="border-t border-slate-200 bg-slate-50/50">
                <td className="p-3 flex items-center">
                  <RotateCcw className="w-4 h-4 mr-2 text-purple-500" />
                  Returns Risk
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    exposure.returnsRisk.level === 'High' ? 'bg-red-100 text-red-700' :
                    exposure.returnsRisk.level === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {exposure.returnsRisk.level}
                  </span>
                </td>
                <td className="p-3 text-slate-600">{exposure.returnsRisk.detail}</td>
                <td className="p-3 text-slate-500">{exposure.returnsRisk.source}</td>
              </tr>
              <tr className="border-t border-slate-200">
                <td className="p-3 flex items-center">
                  <Truck className="w-4 h-4 mr-2 text-teal-500" />
                  Vendor Risk
                </td>
                <td className="p-3">
                  <span className="px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">
                    {exposure.vendorRisk.level}
                  </span>
                </td>
                <td className="p-3 text-slate-600">{exposure.vendorRisk.detail}</td>
                <td className="p-3 text-slate-500">{exposure.vendorRisk.source}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ===== E) 7/30/90 DAY ACTION OUTLOOK ===== */}
      <section className="mb-8" data-testid="action-outlook-section">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Action Outlook</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Next 7 Days */}
          <Card className="border-red-200 bg-red-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-red-800 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Next 7 Days (Containment)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-2">
                {getOutlookBullets('7days').map((bullet, i) => (
                  <li key={i} className="text-sm text-slate-700 flex items-start">
                    <ChevronRight className="w-4 h-4 mr-1 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>{getTranslated(`outlook_7_${i}`, bullet)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Next 30 Days */}
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-amber-800 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2" />
                Next 30 Days (Stabilize)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-2">
                {getOutlookBullets('30days').map((bullet, i) => (
                  <li key={i} className="text-sm text-slate-700 flex items-start">
                    <ChevronRight className="w-4 h-4 mr-1 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>{getTranslated(`outlook_30_${i}`, bullet)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Next 90 Days */}
          <Card className="border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-blue-800 flex items-center">
                <BarChart3 className="w-4 h-4 mr-2" />
                Next 90 Days (Improve)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-2">
                {getOutlookBullets('90days').map((bullet, i) => (
                  <li key={i} className="text-sm text-slate-700 flex items-start">
                    <ChevronRight className="w-4 h-4 mr-1 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{getTranslated(`outlook_90_${i}`, bullet)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ===== F) SUPPORTING EVIDENCE (ACCORDION) ===== */}
      <section className="mb-8" data-testid="supporting-evidence-section">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Supporting Evidence</h2>
        <div className="space-y-2">
          {evidenceSections.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSections[section.id];
            
            return (
              <div key={section.id} className="border border-slate-200 rounded-lg overflow-hidden">
                {/* Accordion Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-4 bg-white hover:bg-slate-50 transition-colors"
                  data-testid={`evidence-section-${section.id}`}
                >
                  <div className="flex items-center">
                    <Icon className={`w-5 h-5 mr-3 text-${section.color}-500`} />
                    <span className="font-medium text-slate-800">{section.title}</span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Accordion Content */}
                {isExpanded && (
                  <div className="p-4 pt-0 bg-slate-50 border-t border-slate-200">
                    <p className="text-sm text-slate-600 mb-3">{section.content}</p>
                    <div className="flex items-center gap-3">
                      {section.agents.map(agent => (
                        <Button
                          key={agent}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => viewEvidence(agent)}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          {agent} Dashboard
                        </Button>
                      ))}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                        onClick={() => downloadEvidence(section.id, section.agents)}
                      >
                        <FileSpreadsheet className="w-3 h-3 mr-1" />
                        Download (English)
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default ExecutiveSummary;
